import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Flex, 
  Spinner, 
  useColorModeValue, 
  Text, 
  Button, 
  HStack, 
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  IconButton,
  Divider,
  Image,
  Link,
  Heading
} from '@chakra-ui/react';
import AnimatedBox from '../AnimatedBox';
import DashboardIntroNotification from '../DashboardIntroNotification';
import TotalReviewsDashboard from './TotalReviewsDashboard';
import GrossGrowthDashboard from './GrossGrowthDashboard';
import SentimentDashboard from './SentimentDashboard';
import WebTrafficDashboard from './WebTrafficDashboard';
import BusinessInsightsDashboard from './BusinessInsightsDashboard';
import MarketIntelligenceDashboard from './CompetitiveIntelDashboard';
import KeywordIntelligenceDashboard from './KeywordIntelligenceDashboard';
import AdsDashboard from './AdsDashboard';
import MarketTrendsDashboard from './MarketTrendsDashboard';
import FavoriteDashboard from './FavoriteDashboard';
import LocalyticsScoreDashboard from './LocalyticsScoreDashboard';
import SetGoalsPage from '../playbook/SetGoalsPage';
import MetricsInsightsPage from '../playbook/MetricsInsightsPage';
import PlaybookActionsPage from '../playbook/PlaybookActionsPage';
import PlaybookReadingPage from '../playbook/PlaybookReadingPage';
import PricingDashboard from './PricingDashboard';
import ContentTemplatesDashboard from './ContentTemplatesDashboard';
import SpyfuKeywordDashboard from './SpyfuKeywordDashboard';
import KeywordsDashboard from './KeywordsDashboard';
import OrganicPaidTrafficDashboard from './OrganicPaidTrafficDashboard';
import CompetitorMapDashboard from './CompetitorMapDashboard';
import RevenueEstimationDashboard from './RevenueEstimationDashboard';
import RevenueOpportunityDashboard from './RevenueOpportunityDashboard';
import { getReviews } from '../../services/reviewService';
import { getYearData } from '../../services/yearService';
import { trackDashboardView } from '../../services/userService';
import {
  FaCalendarAlt,
  FaChevronDown,
  FaTools,
  FaChartArea,
  FaChartLine,
  FaSmile,
  FaGlobe,
  FaTasks,
  FaSearchDollar,
  FaBullhorn,
  FaStar,
  FaBook,
  FaBullseye,
  FaCalculator,
  FaTrophy,
  FaImage
} from 'react-icons/fa';
import { FaDollarSign, FaMapMarkedAlt } from 'react-icons/fa';
import { MdTrendingUp } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useUser } from '../../contexts/UserContext';
import { logger } from '../../utils/logger';

// Map dashboard types to names, icons, descriptions, section labels, and header configurations
const DASHBOARD_INFO = {
  'business-insights': { 
    name: 'Business Insights Dashboard', 
    icon: FaTools,
    description: 'All local businesses ranked by reviews, ratings, and market presence. See who\'s leading and where gaps exist.',
    sectionLabel: 'Business Insights Dashboard',
    header: null // No champion header for this dashboard
  },
  'market-intelligence': {
    name: 'Market Intelligence Dashboard',
    icon: FaSearchDollar,
    description: 'Live competitive data showing how each business is performing right now across key metrics.',
    sectionLabel: 'Market Intelligence Dashboard',
    header: null
  },
  'total-reviews': { 
    name: 'Total Reviews', 
    icon: FaChartArea,
    description: 'Businesses ranked by total review count. The leaders here have built the strongest social proof in your market.',
    sectionLabel: 'Total Reviews',
    header: {
      title: 'Market Authority',
      icon: FaTrophy,
      gradientFrom: 'blue.500',
      gradientTo: 'purple.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'purple.600'
    }
  },
  'gross-growth': { 
    name: 'Review Growth', 
    icon: FaChartLine,
    description: 'New reviews gained over the selected period. Shows which businesses are actively growing their reputation.',
    sectionLabel: 'Review Growth',
    header: {
      title: 'Growth Champions',
      icon: FaTrophy,
      gradientFrom: 'blue.500',
      gradientTo: 'purple.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'purple.600'
    }
  },
  'sentiment': { 
    name: 'Customer Sentiment Analysis', 
    icon: FaSmile,
    description: 'Positive, neutral, and negative review breakdowns for each business. See who customers love and who they\'re warning others about.',
    sectionLabel: 'Customer Sentiment Analysis',
    header: {
      title: 'Sentiment Leaders',
      icon: FaSmile,
      gradientFrom: 'green.500',
      gradientTo: 'teal.500',
      textGradientFrom: 'green.600',
      textGradientTo: 'teal.600'
    }
  },
  'market-trends': {
    name: 'Month in Review',
    icon: MdTrendingUp,
    description: 'This month\'s review activity, rating changes, and notable shifts across the market.',
    sectionLabel: 'Month in Review',
    header: {
      title: 'Month in Review',
      icon: MdTrendingUp,
      gradientFrom: 'blue.500',
      gradientTo: 'teal.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'teal.600'
    }
  },
  'web-traffic': {
    name: 'SEO',
    icon: FaGlobe,
    description: 'Search visibility and ranking data for businesses in your market. See who\'s winning in local search.',
    sectionLabel: 'SEO',
    header: null
  },
  'keyword-intelligence': {
    name: 'Traffic',
    icon: FaGlobe,
    description: 'Website visitor estimates and traffic trends. Shows which businesses are capturing the most online attention.',
    sectionLabel: 'Traffic',
    header: {
      title: 'Traffic Champions',
      icon: FaGlobe,
      gradientFrom: 'blue.500',
      gradientTo: 'cyan.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'cyan.600'
    }
  },
  'keywords': {
    name: 'Keywords',
    icon: FaSearchDollar,
    description: 'Keyword rankings and opportunities across your competitive landscape. Discover high-value keywords and untapped search opportunities.',
    sectionLabel: 'Keywords',
    header: {
      title: 'Keywords',
      icon: FaSearchDollar,
      gradientFrom: 'purple.500',
      gradientTo: 'pink.500',
      textGradientFrom: 'purple.600',
      textGradientTo: 'pink.600'
    }
  },
  'ads-intelligence': {
    name: 'Google Ads',
    icon: FaBullhorn,
    description: 'Paid advertising activity detected across competitors. See who\'s running ads and estimated spend levels.',
    sectionLabel: 'Google Ads',
    header: {
      title: 'Google Ads',
      icon: FaBullhorn,
      gradientFrom: 'pink.500',
      gradientTo: 'purple.500',
      textGradientFrom: 'pink.600',
      textGradientTo: 'purple.600'
    }
  },
  'playbook-goals': {
    name: 'Set Goals',
    icon: FaBullseye,
    description: 'Your targets for reviews, ratings, and market position. Track progress toward each goal.',
    sectionLabel: 'Set Goals',
    header: {
      title: 'Goals',
      icon: FaBullseye,
      gradientFrom: 'orange.500',
      gradientTo: 'yellow.500',
      textGradientFrom: 'orange.600',
      textGradientTo: 'yellow.600'
    }
  },
  'playbook-actions': {
    name: 'Playbook: Actions',
    icon: FaTasks,
    description: 'Recommended next steps based on current market data and your competitive position.',
    sectionLabel: 'Playbook: Actions',
    header: null
  },
  'playbook-reading': {
    name: 'Playbook: Reading List',
    icon: FaBook,
    description: 'Recent reviews and customer feedback worth reading. Highlights common themes and notable comments.',
    sectionLabel: 'Playbook: Reading List',
    header: {
      title: 'Reading List',
      icon: FaBook,
      gradientFrom: 'green.500',
      gradientTo: 'teal.500',
      textGradientFrom: 'green.600',
      textGradientTo: 'teal.600'
    }
  },
  'favorites': {
    name: 'My Favorite',
    icon: FaStar,
    description: 'Your selected business compared to market averages and the current leader in each category.',
    sectionLabel: 'My Favorite',
    header: {
      title: 'My Favorite',
      icon: FaStar,
      gradientFrom: 'blue.500',
      gradientTo: 'purple.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'purple.600'
    }
  },
  'localytics-score': {
    name: 'Localytics Score',
    icon: FaCalculator,
    description: 'Performance scores (0-100) based on growth, reviews, traffic, sentiment, and SEO. Higher score indicates stronger market position.',
    sectionLabel: 'Localytics Score Analysis',
    header: {
      title: 'Score Champions',
      icon: FaTrophy,
      gradientFrom: 'purple.500',
      gradientTo: 'blue.500',
      textGradientFrom: 'purple.600',
      textGradientTo: 'blue.600'
    }
  },
  'pricing': {
    name: 'Pricing Analysis',
    icon: FaDollarSign,
    description: 'Price points and cost mentions extracted from reviews. See how businesses are positioned on value.',
    sectionLabel: 'Pricing Intelligence'
  },
  'content-templates': {
    name: 'Content Templates',
    icon: FaImage,
    description: 'Ready-to-use marketing templates generated from your business data and market insights.',
    sectionLabel: 'Content Templates',
    header: {
      title: 'Content Strategy',
      icon: FaImage,
      gradientFrom: 'blue.400',
      gradientTo: 'cyan.500',
      textGradientFrom: 'blue.500',
      textGradientTo: 'cyan.600'
    }
  },
  'spyfu-keywords': {
    name: 'Keyword Intelligence',
    icon: FaSearchDollar,
    description: 'Keywords your competitors rank for and their estimated search traffic value.',
    sectionLabel: 'Keyword Intelligence',
    header: {
      title: 'Keyword Leaders',
      icon: FaSearchDollar,
      gradientFrom: 'purple.500',
      gradientTo: 'blue.500',
      textGradientFrom: 'purple.600',
      textGradientTo: 'blue.600'
    }
  },
  'organic-paid-traffic': {
    name: 'Organic vs Paid Traffic',
    icon: FaChartArea,
    description: 'Compare organic search traffic versus paid advertising performance across businesses in your market.',
    sectionLabel: 'Organic vs Paid Traffic',
    header: {
      title: 'Organic vs Paid',
      icon: FaChartArea,
      gradientFrom: 'green.500',
      gradientTo: 'yellow.500',
      textGradientFrom: 'green.600',
      textGradientTo: 'yellow.600'
    }
  },
  'competitor-map': {
    name: 'Competitor Map',
    icon: FaMapMarkedAlt,
    description: 'Interactive map showing all competitors in your market with location pins and business intelligence on hover.',
    sectionLabel: 'Competitor Map',
    header: {
      title: 'Competitor Map',
      icon: FaMapMarkedAlt,
      gradientFrom: 'blue.500',
      gradientTo: 'cyan.500',
      textGradientFrom: 'blue.600',
      textGradientTo: 'cyan.600'
    }
  },
  'revenue-estimation': {
    name: 'Revenue Estimation',
    icon: FaDollarSign,
    description: 'AI-powered revenue estimates for each business based on reviews, web traffic, and competitive intelligence data.',
    sectionLabel: 'Revenue Estimation',
    header: {
      title: 'Revenue Estimation',
      icon: FaDollarSign,
      gradientFrom: 'green.500',
      gradientTo: 'teal.500',
      textGradientFrom: 'green.600',
      textGradientTo: 'teal.600'
    }
  },
  'revenue-opportunity': {
    name: 'Revenue Opportunity',
    icon: FaDollarSign,
    description: 'Estimate your local market size, likely market share, and the customers, traffic, reviews and ad performance needed to hit your revenue goal.',
    sectionLabel: 'Local Revenue Opportunity Model',
    header: {
      title: 'Revenue Opportunity',
      icon: FaDollarSign,
      gradientFrom: 'purple.500',
      gradientTo: 'blue.500',
      textGradientFrom: 'purple.600',
      textGradientTo: 'blue.600'
    }
  },
  // 'metrics-insights': {
  //   name: 'Metrics & Analysis',
  //   icon: FaBrain,
  //   description: 'In-depth quantitative and qualitative metric analysis with actionable strategies.',
  //   sectionLabel: 'Playbook: Metrics & Analysis'
  // }
};

// Section Label component to be used across dashboards
export const SectionLabel = ({ children, description, ...props }) => {
  const dividerColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const descriptionColor = useColorModeValue("gray.500", "gray.400");
  
  return (
    <AnimatedBox 
      animation="fadeIn"
      delay={0.2}
      duration={0.7}
      py={4} 
      my={4} 
      width="100%" 
      {...props}
    >
      <HStack spacing={4} mb={description ? 2 : 0}>
        <Divider flex={1} borderColor={dividerColor} />
        <AnimatedBox
          animation="pulse"
          delay={0.4}
          duration={0.7}
        >
          <Text 
            fontSize="lg" 
            fontWeight="500" 
            color={textColor}
            whiteSpace="nowrap"
            px={2}
          >
            {children}
          </Text>
        </AnimatedBox>
        <Divider flex={1} borderColor={dividerColor} />
      </HStack>
      
      {description && (
        <AnimatedBox
          animation="slideUp"
          delay={0.5}
          duration={0.5}
        >
          <Text 
            fontSize="md" 
            color={descriptionColor} 
            textAlign="center"
            px={4}
          >
            {description}
          </Text>
        </AnimatedBox>
      )}
    </AnimatedBox>
  );
};

const DashboardWrapper = ({ type }) => {
  const { brandName } = useUser();
  const [reviewsData, setReviewsData] = useState([]);
  const [yearData, setYearData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const dashboardInfo = useMemo(() => ({
    ...DASHBOARD_INFO,
    'localytics-score': {
      ...DASHBOARD_INFO['localytics-score'],
      name: `${brandName} Score`,
      sectionLabel: `${brandName} Score Analysis`,
    },
  }), [brandName]);
  
  // Set default timeframe based on dashboard type
  // Review Growth dashboard defaults to 90 days, others to last year
  const getDefaultTimeframe = () => {
    if (type === 'gross-growth') {
      return 'last90days';
    }
    return 'lastyear';
  };
  
  const getDefaultDateRange = () => {
    if (type === 'gross-growth') {
      return {
        startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
      };
    }
    return {
      startDate: moment().subtract(1, 'year').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD')
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [selectedTimeframe, setSelectedTimeframe] = useState(getDefaultTimeframe);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const menuBg = useColorModeValue('white', 'gray.700');
  const menuBorder = useColorModeValue('gray.200', 'gray.600');
  const selectBg = useColorModeValue('white', 'gray.700');
  const selectBorder = useColorModeValue('gray.200', 'gray.600');
  const navigate = useNavigate();

  // Track dashboard view when type changes
  useEffect(() => {
    if (type) {
      trackDashboardView(type, { timeframe: selectedTimeframe });
    }
  }, [type]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [data, yearDataResponse] = await Promise.all([
          getReviews(),
          getYearData().catch(err => {
            logger.error('Error fetching year data:', err);
            return [];
          })
        ]);

        setYearData(yearDataResponse || []);
        
        if (!data) {
          throw new Error('No data received from the server');
        }
        
        // Apply date filtering on the client side
        const filteredData = data.map(business => {
          // Create a shallow copy to avoid mutating the original
          const filteredBusiness = { ...business };
          
          // Deep copy only the nested data object that we'll mutate
          if (business.data) {
            filteredBusiness.data = { ...business.data };
          }
          
          // Ensure the rating_count property is present
          if (typeof filteredBusiness.rating_count === 'undefined') {
            filteredBusiness.rating_count = business.rating_count || 0;
          }
          
          // Only filter if we have historical data and a date range
          if (dateRange.startDate && dateRange.endDate && 
              business.data && business.data.monthlyReviewCounts) {
            
            // Filter monthly counts
            const filteredMonthlyReviewCounts = {};
            const filteredMonthlyRatings = {};
            
            Object.entries(business.data.monthlyReviewCounts).forEach(([month, count]) => {
              const monthDate = moment(month);
              if (monthDate.isBetween(dateRange.startDate, dateRange.endDate, 'day', '[]')) {
                filteredMonthlyReviewCounts[month] = count;
                if (business.data.monthlyRatings && business.data.monthlyRatings[month]) {
                  filteredMonthlyRatings[month] = business.data.monthlyRatings[month];
                }
              }
            });
            
            // Update the filtered business data
            filteredBusiness.data.monthlyReviewCounts = filteredMonthlyReviewCounts;
            filteredBusiness.data.monthlyRatings = filteredMonthlyRatings;
            
            // Recalculate totals based on filtered data
            const totalPositive = Object.values(filteredMonthlyRatings)
              .reduce((sum, rating) => sum + (rating.positive || 0), 0);
            const totalNeutral = Object.values(filteredMonthlyRatings)
              .reduce((sum, rating) => sum + (rating.neutral || 0), 0);
            const totalNegative = Object.values(filteredMonthlyRatings)
              .reduce((sum, rating) => sum + (rating.negative || 0), 0);
            
            filteredBusiness.data.totalReviews = totalPositive + totalNeutral + totalNegative;
            
            // Update sentiment breakdown
            filteredBusiness.data.sentimentBreakdown = {
              positive: totalPositive,
              neutral: totalNeutral,
              negative: totalNegative
            };
          }
          
          return filteredBusiness;
        }).filter(business => business !== null);
        
        // Add check to ensure all businesses have a rating_count property
        const validData = filteredData.map(business => {
          if (!business) return null;
          
          // If rating_count is undefined, set a default value of 0
          if (typeof business.rating_count === 'undefined') {
            business.rating_count = 0;
          }
          
          return business;
        }).filter(business => business !== null);
        
        setReviewsData(validData);
        logger.log(`Filtered data for date range: ${dateRange.startDate} to ${dateRange.endDate}`);
      } catch (error) {
        logger.error("Error fetching dashboard data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange.startDate, dateRange.endDate]);

  const handleTimeframeChange = (event) => {
    const value = event.target.value;
    setSelectedTimeframe(value);
    
    const endDate = moment().format('YYYY-MM-DD');
    let startDate;
    
    switch (value) {
      case 'last30days':
        startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        break;
      case 'last90days':
        startDate = moment().subtract(90, 'days').format('YYYY-MM-DD');
        break;
      case 'last6months':
        startDate = moment().subtract(6, 'months').format('YYYY-MM-DD');
        break;
      case 'lastyear':
        startDate = moment().subtract(1, 'year').format('YYYY-MM-DD');
        break;
      case 'yeartodate':
        startDate = moment().startOf('year').format('YYYY-MM-DD');
        break;
      default:
        startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
    }
    
    setDateRange({ startDate, endDate });
  };

  // Data is already filtered in the useEffect above
  const filteredReviewsData = reviewsData;

  // Navigation bar with dashboard options
  const DashboardNav = () => {
    const types = Object.keys(dashboardInfo);
    
    return (
      <AnimatedBox animation="fadeIn" duration={0.5}>
        <Menu>
          <MenuButton
            as={Button}
            rightIcon={<Icon as={FaChevronDown} />}
            leftIcon={<Icon as={dashboardInfo[type]?.icon || FaTools} />}
            bg={menuBg}
            border="1px"
            borderColor={menuBorder}
            _hover={{ 
              bg: useColorModeValue('gray.50', 'gray.700'),
              transform: "translateY(-2px)",
              shadow: "md"
            }}
            _active={{ bg: useColorModeValue('gray.100', 'gray.600') }}
            size="md"
            transition="all 0.2s"
          >
            <AnimatedBox animation="pulse" delay={0.2} duration={0.5}>
              {dashboardInfo[type]?.name || 'Dashboard'}
            </AnimatedBox>
          </MenuButton>
          <MenuList bg={menuBg} borderColor={menuBorder} zIndex="dropdown">
            {types.map((dashType, index) => (
              <AnimatedBox
                key={dashType}
                as={MenuItem}
                animation="slideLeft"
                delay={0.03 * index}
                duration={0.3}
                icon={<Icon as={dashboardInfo[dashType].icon} />}
                onClick={() => {
                  if (dashType === 'metrics-insights') {
                    navigate('/metrics');
                  } else if (dashType.startsWith('playbook-')) {
                    const playbookPage = dashType.substring('playbook-'.length);
                    navigate(`/playbook/${playbookPage}`);
                  } else {
                    navigate(`/dashboards/${dashType}`);
                  }
                }}
                bg={dashType === type ? 'gray.50' : 'transparent'}
                fontWeight={dashType === type ? 'bold' : 'normal'}
                transition="all 0.2s"
                _hover={{ bg: dashType === type ? 'gray.100' : 'gray.50' }}
              >
                {dashboardInfo[dashType].name}
              </AnimatedBox>
            ))}
          </MenuList>
        </Menu>
      </AnimatedBox>
    );
  };

  const renderDashboard = () => {
    const dashboardProps = {
      reviewsData: filteredReviewsData,
      dateRange,
      sectionLabel: dashboardInfo[type]?.sectionLabel,
      yearData,
    };

    if (error) {
      return <Text color="red.500">Error loading data: {error}</Text>;
    }

    switch (type) {
      case 'total-reviews':
        return <TotalReviewsDashboard {...dashboardProps} />;
      case 'gross-growth':
        return <GrossGrowthDashboard {...dashboardProps} />;
      case 'sentiment':
        return <SentimentDashboard {...dashboardProps} />;
      case 'market-trends':
        return <MarketTrendsDashboard {...dashboardProps} />;
      case 'web-traffic':
        return <WebTrafficDashboard {...dashboardProps} />;
      case 'business-insights':
        return <BusinessInsightsDashboard {...dashboardProps} />;
      case 'market-intelligence':
        return <MarketIntelligenceDashboard {...dashboardProps} />;
      case 'ads-intelligence':
        return <AdsDashboard {...dashboardProps} />;
      case 'keyword-intelligence':
        return <KeywordIntelligenceDashboard {...dashboardProps} />;
      case 'keywords':
        return <KeywordsDashboard {...dashboardProps} />;
      case 'playbook-goals':
        return <SetGoalsPage {...dashboardProps} />;
      case 'playbook-actions':
        return <PlaybookActionsPage {...dashboardProps} />;
      case 'playbook-reading':
        return <PlaybookReadingPage {...dashboardProps} />;
      case 'favorites':
        return <FavoriteDashboard {...dashboardProps} />;
      case 'localytics-score':
        return <LocalyticsScoreDashboard />;
      case 'pricing':
        return <PricingDashboard />;
      case 'content-templates':
        return <ContentTemplatesDashboard {...dashboardProps} />;
      case 'spyfu-keywords':
        return <SpyfuKeywordDashboard {...dashboardProps} />;
      case 'organic-paid-traffic':
        return <OrganicPaidTrafficDashboard {...dashboardProps} />;
      case 'competitor-map':
        return <CompetitorMapDashboard {...dashboardProps} />;
      case 'revenue-estimation':
        return <RevenueEstimationDashboard {...dashboardProps} />;
      case 'revenue-opportunity':
        return <RevenueOpportunityDashboard {...dashboardProps} />;
      default:
        return <Text>Invalid dashboard type</Text>;
    }
  };

  // Get current dashboard info for the intro notification
  const currentDashboardInfo = dashboardInfo[type];

  const isFullScreenMap = type === 'competitor-map';

  return (
    <Box
      position="relative"
      bg={isFullScreenMap ? 'transparent' : cardBg}
      borderRadius={isFullScreenMap ? 0 : { base: "md", md: "lg" }}
      boxShadow={isFullScreenMap ? 'none' : 'sm'}
      p={isFullScreenMap ? 0 : { base: 3, md: 6 }}
      ml={0}
      maxW="none"
      minH={isFullScreenMap ? undefined : "calc(100vh - 2rem)"}
      borderColor={isFullScreenMap ? 'transparent' : borderColor}
      borderWidth={isFullScreenMap ? 0 : "1px"}
    >
      {/* Dashboard Intro Notification - shows first time each dashboard is viewed in a session */}
      {currentDashboardInfo && (
        <DashboardIntroNotification
          dashboardType={type}
          title={currentDashboardInfo.name}
          description={currentDashboardInfo.description}
          icon={currentDashboardInfo.icon}
          color={currentDashboardInfo.header?.gradientFrom?.split('.')[0] || 'blue'}
        />
      )}

      {/* Navigation Bar and Title - Hidden for full-screen map */}
      <Box mb={isFullScreenMap ? 0 : 6} display={isFullScreenMap ? 'none' : 'block'}>
        <Flex 
          justifyContent="space-between" 
          alignItems="center" 
          mb={3}
          flexDirection={{ base: "column", sm: "row" }}
          gap={4}
        >
          {/* Dashboard Header - displayed in top left corner */}
          {dashboardInfo[type]?.header ? (
            <HStack spacing={2} align="center">
              <Box
                bgGradient={`linear(to-r, ${dashboardInfo[type].header.gradientFrom}, ${dashboardInfo[type].header.gradientTo})`}
                p={2}
                borderRadius="lg"
              >
                <Icon as={dashboardInfo[type].header.icon} color="white" boxSize={5} />
              </Box>
              <Heading 
                size="lg" 
                bgGradient={`linear(to-r, ${dashboardInfo[type].header.textGradientFrom}, ${dashboardInfo[type].header.textGradientTo})`}
                bgClip="text"
                fontWeight="bold"
              >
                {dashboardInfo[type].header.title}
              </Heading>
            </HStack>
          ) : (
            <HStack spacing={4}>
              {/* Empty space for dashboards without headers */}
            </HStack>
          )}
          
          {/* Date selector for dashboards that support range filtering */}
          {/* Positioned below and left of the AI chat button with proper spacing */}
          {type !== 'business-insights' && type !== 'total-reviews' && type !== 'web-traffic' && type !== 'playbook-actions' && type !== 'playbook-goals' && type !== 'playbook-reading' && type !== 'pricing' && type !== 'market-trends' && type !== 'competitor-map' && (
            <HStack spacing={2} ml="auto" mr={{ base: 0, md: "70px" }}>
              <Icon as={FaCalendarAlt} color="gray.500" />
              <Select
                value={selectedTimeframe}
                onChange={handleTimeframeChange}
                width={{ base: "full", sm: "200px" }}
                size="md"
                bg={selectBg}
                borderColor={selectBorder}
                _hover={{ borderColor: 'blue.400' }}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
              >
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="lastyear">Last Year</option>
                <option value="yeartodate">Year to Date</option>
              </Select>
            </HStack>
          )}
        </Flex>
        
        {/* We're removing the top description since it's now in the section labels */}
      </Box>

      {isLoading ? (
        <Flex 
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          justify="center" 
          align="center"
          bg={`${cardBg}dd`}
          borderRadius="lg"
        >
          <AnimatedBox
            animation="fadeIn"
            duration={0.8}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Spinner 
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="blue.500"
              size="xl"
            />
            <AnimatedBox 
              animation="pulse" 
              delay={0.3}
              duration={1.5}
              repeat={true}
            >
              <Text mt={4} fontSize="lg" color="gray.500">Loading dashboard data...</Text>
            </AnimatedBox>
          </AnimatedBox>
        </Flex>
      ) : (
        <AnimatedBox
          animation="fadeIn"
          duration={0.6}
          width="100%"
        >
          {renderDashboard()}
        </AnimatedBox>
      )}
    </Box>
  );
};

export default DashboardWrapper;